/** Ported from chief's `web/src/data.tsx` (MIT) — see "Licence and attribution" in README.md. */

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ApiError,
  type ClaudeState,
  type Content,
  fetchClaudeState,
  fetchContentList,
  fetchRepositories,
  fetchStats,
  fetchStorage,
  type Repository,
  type Stats,
  type StorageSettings,
  type ToneOption,
} from './api.ts';
import { notifyingWhileHidden } from './notifications.ts';
import { startsIn } from './schedule.ts';
import {
  ATTENTION_MISSED_START,
  ATTENTION_NARRATION,
  ATTENTION_ON_HOLD,
  ATTENTION_PLAN,
  ATTENTION_PLANNER_ASKED,
  ATTENTION_PLANNING_STOPPED,
  ATTENTION_SCENES,
  ATTENTION_SETUP_FAILED,
  ATTENTION_START_PLANNING,
  ATTENTION_START_PRODUCTION,
  attentionFailed,
  attentionOnHold,
  attentionPostsDue,
  attentionReview,
} from './wording.ts';

/**
 * The app-wide data every authenticated page reads.
 *
 * Contents are moved along by the production loop and the scheduler — both in
 * other processes, neither able to reach this tab — so the only way to show a
 * change is to keep looking. One poll here feeds the sidebar's facts and the
 * overview at once, rather than each page running its own. A hidden tab polls
 * nothing — except the content list while notifications are on (US-034) —
 * and coming back re-reads at once.
 */

/**
 * The dashboard's cadence. Exported because pages that poll a list of their
 * own refresh on the same beat rather than inventing a second one.
 */
export const DASHBOARD_POLL_MS = 3000;
const STATS_POLL_MS = 5000;

export interface AppData {
  /** Every content, newest first; `null` until the first read lands. */
  readonly contents: Content[] | null;
  readonly repositories: Repository[] | null;
  /** The tones the server offers, in its order; they name a content's tone. */
  readonly tones: ToneOption[] | null;
  readonly stats: Stats | null;
  readonly claude: ClaudeState | null;
  /** The bucket every asset is delivered to; `null` until the first read. */
  readonly storage: StorageSettings | null;
  /** The last failure to read the lists; the previous data stays on screen. */
  readonly error: string | null;
  /** Re-reads the lists and the stats now, after an action changed something. */
  readonly refresh: () => Promise<void>;
  /**
   * Replaces one content in the cached list with the server's reply to an
   * action, so its row changes now rather than on the next poll.
   */
  readonly setContent: (content: Content) => void;
  /** Replaces the cached Claude status, e.g. after a login. */
  readonly setClaude: (state: ClaudeState) => void;
  /** Replaces the cached storage settings, after a save or a test. */
  readonly setStorage: (settings: StorageSettings) => void;
}

const Context = createContext<AppData | null>(null);

export function describeError(error: unknown): string {
  return error instanceof ApiError ? error.message : String(error);
}

/** `401` on any read means the cookie expired: back to the login page. */
export function redirectIfUnauthorised(error: unknown): boolean {
  if (error instanceof ApiError && error.status === 401) {
    window.location.replace('/login');
    return true;
  }
  return false;
}

export function AppDataProvider({ children }: { readonly children: ReactNode }) {
  const [contents, setContents] = useState<Content[] | null>(null);
  const [repositories, setRepositories] = useState<Repository[] | null>(null);
  const [tones, setTones] = useState<ToneOption[] | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [claude, setClaude] = useState<ClaudeState | null>(null);
  const [storage, setStorage] = useState<StorageSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * The signal of the read in flight, so a poll never stacks behind a slow
   * one. Keyed on the signal rather than a boolean: StrictMode aborts the
   * first mount's read and starts the second's before the abort has settled,
   * and a boolean would make the second one wait for the next poll.
   */
  const inFlight = useRef<AbortSignal | null>(null);

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    if (inFlight.current !== null && !inFlight.current.aborted) return;
    inFlight.current = signal ?? new AbortController().signal;
    try {
      // One round trip each, together: the content list is what the list page
      // and the overview both read, and the repositories are what names them.
      const [list, repos] = await Promise.all([fetchContentList(signal), fetchRepositories(signal)]);
      setContents(list.contents);
      setTones(list.tones);
      setRepositories(repos);
      setError(null);
    } catch (cause: unknown) {
      if (signal?.aborted === true) return;
      if (redirectIfUnauthorised(cause)) return;
      setError(describeError(cause));
    } finally {
      inFlight.current = null;
    }
  }, []);

  const loadStats = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      setStats(await fetchStats(signal));
    } catch (cause: unknown) {
      if (signal?.aborted === true) return;
      redirectIfUnauthorised(cause);
      // The overview says so itself; the shell keeps the last numbers.
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const visible = (): boolean => document.visibilityState === 'visible';

    void load(controller.signal);
    void loadStats(controller.signal);
    // The list alone keeps going in a hidden tab while notifications are on:
    // that is when they are shown (US-034), and the stats are not needed for it.
    const listTimer = window.setInterval(() => {
      if (visible() || notifyingWhileHidden()) void load(controller.signal);
    }, DASHBOARD_POLL_MS);
    const statsTimer = window.setInterval(() => {
      if (visible()) void loadStats(controller.signal);
    }, STATS_POLL_MS);
    const onVisible = (): void => {
      if (!visible()) return;
      void load(controller.signal);
      void loadStats(controller.signal);
    };
    document.addEventListener('visibilitychange', onVisible);

    // Producing anything needs a signed-in Claude Code. Checked once: a probe
    // costs a container start, so it is not polled.
    fetchClaudeState({ signal: controller.signal })
      .then(setClaude)
      .catch(() => {
        // The settings page reports why; the shell stays quiet.
      });

    // The bucket, for the set-up list. Read once for the same reason: it only
    // changes when someone saves the panel, and the panel says so itself.
    fetchStorage(controller.signal)
      .then((view) => {
        setStorage(view.storage);
      })
      .catch(() => {
        // Same: an unreadable settings row is the settings page's news.
      });

    return () => {
      controller.abort();
      window.clearInterval(listTimer);
      window.clearInterval(statsTimer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load, loadStats]);

  const refresh = useCallback(async (): Promise<void> => {
    await Promise.all([load(), loadStats()]);
  }, [load, loadStats]);

  const setContent = useCallback((content: Content): void => {
    setContents((list) =>
      list === null ? list : list.map((one) => (one.id === content.id ? content : one)),
    );
  }, []);

  const value = useMemo<AppData>(
    () => ({
      contents,
      repositories,
      tones,
      stats,
      claude,
      storage,
      error,
      refresh,
      setContent,
      setClaude,
      setStorage,
    }),
    [contents, repositories, tones, stats, claude, storage, error, refresh, setContent],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useAppData(): AppData {
  const value = useContext(Context);
  if (value === null) throw new Error('useAppData() outside <AppDataProvider>');
  return value;
}

/**
 * Why the operator has to do something about a content, in a few words, or
 * `null` when nothing waits on them: it failed, it is held, its scheduled
 * start went by, it sits at a gate only a person can pass, the planner asked
 * something, it waits to be started, or a post of it is due. The checks run in
 * order; the first one that matches wins.
 *
 * One function answers both "does it need me" and "why", so the overview, the
 * list's badge, the sidebar count and the “Needs you” filter never disagree.
 */
export function attentionReason(content: Content): string | null {
  // Archived work is off the board, the calendar and every "needs you".
  if (content.archivedAt !== null) return null;
  if (content.status === 'failed') {
    return attentionFailed(content.failureStage);
  }
  if (content.status === 'waiting') {
    return content.waitingUntil === null
      ? ATTENTION_ON_HOLD
      : attentionOnHold(startsIn(content.waitingUntil).replace('starts ', ''));
  }
  if (content.scheduleMissed) return ATTENTION_MISSED_START;
  // Claude at work on what the operator handed back — their feedback on a
  // version, the scenes being fitted or adjusted, an automatic round — is not
  // a decision of theirs yet: it counts as Active (`isWorking`), not here.
  if (isWorking(content)) return null;
  if (content.status === 'approval') {
    return content.gate === 'sketch' ? ATTENTION_SCENES : ATTENTION_NARRATION;
  }
  if (content.status === 'review') return attentionReview(content.currentVersion);
  if (content.status === 'pending') {
    if (content.lastError !== null) return ATTENTION_SETUP_FAILED;
    if (content.planParses) return ATTENTION_PLAN;
    if (content.planningState === 'your-turn') return ATTENTION_PLANNER_ASKED;
    if (content.planningState === 'failed') return ATTENTION_PLANNING_STOPPED;
    // Set up, and nobody is planning it yet.
    if (content.planningState === 'none' && content.containerId !== null) return ATTENTION_START_PLANNING;
    return null;
  }
  if (content.status === 'ready' && content.queuePosition === null && content.scheduledStartAt === null) {
    return ATTENTION_START_PRODUCTION;
  }
  if (content.status === 'delivered' && content.posts.due > 0) return attentionPostsDue(content.posts.due);
  return null;
}

/**
 * A content the operator has to do something about. This is what the “Needs
 * you” filter and the sidebar count, and what marks a row at its left edge.
 */
export function needsAttention(content: Content): boolean {
  return attentionReason(content) !== null;
}

/** Nothing more will happen on its own: approved, or waiting to be approved. */
export function isEnded(content: Pick<Content, 'status'>): boolean {
  return content.status === 'review' || content.status === 'delivered';
}

/**
 * Claude is busy on a content whose status alone reads as waiting on the
 * operator: a **Request changes** pass rewriting the plan from their feedback
 * (the status stays `review` or `delivered` until production starts again),
 * the sketch gate's scenes being prepared or adjusted, an automatic review
 * round, or the planner answering them.
 */
export function isWorking(content: Content): boolean {
  if (content.archivedAt !== null || content.status === 'failed' || content.status === 'rejected') return false;
  if (content.revising || content.autoReview !== null) return true;
  if (content.status === 'approval') {
    return content.sketchState === 'preparing' || content.sketchState === 'adjusting';
  }
  // A plan that parses already waits on **Mark ready**, and a failed setup on a retry.
  return (
    content.status === 'pending' &&
    content.planningState === 'thinking' &&
    !content.planParses &&
    content.lastError === null
  );
}

/** Work in progress: producing, held, queued behind a production slot, or Claude at work on it. */
export function isActive(content: Content): boolean {
  return (
    content.status === 'producing' ||
    content.status === 'waiting' ||
    content.status === 'approval' ||
    content.queuePosition !== null ||
    isWorking(content)
  );
}

/**
 * Keyboard shortcuts: a leader key (`g`) followed by a letter within a second.
 * A binding that returns `false` leaves the key alone, default action included.
 */
export function useKeyChords(bindings: Record<string, () => unknown>): void {
  const ref = useRef(bindings);
  ref.current = bindings;
  useEffect(() => {
    let leader = 0;
    const onKey = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      const typing =
        target !== null &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable ||
          target.closest('.xterm') !== null);
      if (typing || event.metaKey || event.ctrlKey || event.altKey) return;

      const now = Date.now();
      const key = event.key.toLowerCase();
      if (leader !== 0 && now - leader < 1000) {
        leader = 0;
        const action = ref.current[`g ${key}`];
        if (action !== undefined && action() !== false) event.preventDefault();
        return;
      }
      if (key === 'g') {
        leader = now;
        return;
      }
      const action = ref.current[key];
      if (action !== undefined && action() !== false) event.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, []);
}

/**
 * `lg` (64rem), the breakpoint `shell.css` builds the frame around: below it
 * the sidebar is a drawer, every grid is one column, and the terminal panes
 * are not shown at all.
 */
export const DESKTOP_QUERY = '(min-width: 64rem)';

/**
 * A live media query.
 *
 * CSS decides nearly everything responsive here, but not this: a terminal
 * pane that is merely hidden still mounts xterm.js and opens its WebSocket,
 * so a phone would attach to a PTY it cannot show. Reading the breakpoint in
 * JavaScript is what lets the component not exist below `lg`.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const list = window.matchMedia(query);
    const update = (): void => {
      setMatches(list.matches);
    };
    update();
    list.addEventListener('change', update);
    return () => {
      list.removeEventListener('change', update);
    };
  }, [query]);
  return matches;
}

/**
 * `Date.now()`, re-read on an interval, for the one thing on a page that has
 * to move without the server saying anything: a countdown.
 *
 * Everything else here is redrawn by a poll — a number that is three seconds
 * stale is close enough for a queue position or a step count. A hold the
 * operator is sitting in front of watching drain is not: a clock that stops
 * for three seconds at a time reads as a page that has stopped updating, which
 * is the impression the hold banner exists to prevent.
 */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, intervalMs);
    return () => {
      window.clearInterval(timer);
    };
  }, [intervalMs]);
  return now;
}
